import { useTranslation } from 'react-i18next';

interface SessionTimeoutWarningProps {
  onExtendSession: () => void;
  onLogout: () => void;
  timeRemaining: number; // in seconds
  /**
   * Show the warning at this many seconds remaining. Defaults to 2 minutes;
   * AuthContext passes 20% of the org's timeout when that is under 10 minutes.
   */
  warningSeconds?: number;
}

export function SessionTimeoutWarning({
  onExtendSession,
  onLogout,
  timeRemaining,
  warningSeconds = 120,
}: SessionTimeoutWarningProps) {
  const { t } = useTranslation();

  if (!(timeRemaining > 0 && timeRemaining <= warningSeconds)) return null;

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const countdown = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      className="fixed top-4 right-4 z-50 bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4 max-w-sm"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 text-sm">⚠️</span>
          </div>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            {t('auth.idleTimeout.warningTitle', 'Session Expiring Soon')}
          </h3>
          <p className="text-sm text-yellow-700 mt-1">
            {t('auth.idleTimeout.warningBody', 'Your session will expire in {{countdown}} due to inactivity', {
              countdown,
            })}
          </p>
          <div className="mt-3 flex space-x-2">
            <button
              onClick={onExtendSession}
              className="text-xs bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
            >
              {t('auth.idleTimeout.stayLoggedIn', 'Stay Logged In')}
            </button>
            <button
              onClick={onLogout}
              className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors"
            >
              {t('auth.idleTimeout.logoutNow', 'Logout Now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
